/**
 * Implementation navigateur de `VaultRepository` (IndexedDB). Adaptateur
 * d'E/S pur : aucune logique de coffre ici (voir `vault-store.ts`), juste la
 * lecture/ecriture des memes formes ciphertext-only que `MemoryVaultRepository`
 * (`repository.ts`). Contrat verifie contre `fake-indexeddb`
 * (`test/indexeddb-repository.test.ts`).
 */
import type { EncryptedVaultAttachment, EncryptedVaultFolder, EncryptedVaultItem } from '@klavox/vault-model';
import type { VaultRepository } from './repository.js';
import type { AccountEnvelope, PasskeyUnlockRecord } from './types.js';

const DB_NAME = 'klavox-vault';
/** v2 : ajout du magasin `attachments` (les magasins existants sont conserves tels quels). */
const DB_VERSION = 2;
const STORE_ITEMS = 'items';
const STORE_FOLDERS = 'folders';
const STORE_META = 'meta';
const STORE_ATTACHMENTS = 'attachments';
const INDEX_ATTACHMENT_ITEM = 'itemId';
const META_KEY_ACCOUNT = 'account-envelope';
/** Fiches de deverrouillage par passkey (R12) : propres a cet appareil, jamais synchronisees. */
const META_KEY_PASSKEY_UNLOCKS = 'passkey-unlocks';
/** Identifiant d'appareil (mode voyage, R18) : opaque, non secret, jamais synchronise. */
const META_KEY_DEVICE_ID = 'device-id';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_ITEMS)) db.createObjectStore(STORE_ITEMS, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(STORE_FOLDERS)) db.createObjectStore(STORE_FOLDERS, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(STORE_META)) db.createObjectStore(STORE_META);
      if (!db.objectStoreNames.contains(STORE_ATTACHMENTS)) {
        db.createObjectStore(STORE_ATTACHMENTS, { keyPath: 'id' }).createIndex(INDEX_ATTACHMENT_ITEM, 'itemId');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB: ouverture echouee'));
  });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB: requete echouee'));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB: transaction echouee'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB: transaction annulee'));
  });
}

/**
 * Ecrit dans UNE transaction `readwrite`. IndexedDB n'annule PAS la transaction
 * quand `put` leve de facon synchrone (ex. `DataError` sur un id absent) : les
 * ecritures deja emises seraient validees. On l'annule explicitement pour qu'un
 * lot (synchro, import) soit tout-ou-rien.
 */
async function writeTx(db: IDBDatabase, storeNames: string | string[], write: (tx: IDBTransaction) => void): Promise<void> {
  const tx = db.transaction(storeNames, 'readwrite');
  const done = txDone(tx);
  try {
    write(tx);
  } catch (cause) {
    done.catch(() => undefined);
    tx.abort();
    throw cause;
  }
  await done;
}

export class IndexedDbVaultRepository implements VaultRepository {
  #dbPromise: Promise<IDBDatabase> | null = null;

  #db(): Promise<IDBDatabase> {
    this.#dbPromise ??= openDatabase();
    return this.#dbPromise;
  }

  async loadAccountEnvelope(): Promise<AccountEnvelope | null> {
    const db = await this.#db();
    const tx = db.transaction(STORE_META, 'readonly');
    const value = await requestToPromise(tx.objectStore(STORE_META).get(META_KEY_ACCOUNT));
    return (value as AccountEnvelope | undefined) ?? null;
  }

  async saveAccountEnvelope(envelope: AccountEnvelope): Promise<void> {
    await writeTx(await this.#db(), STORE_META, (tx) => tx.objectStore(STORE_META).put(envelope, META_KEY_ACCOUNT));
  }

  async clearAccountEnvelope(): Promise<void> {
    await writeTx(await this.#db(), STORE_META, (tx) => tx.objectStore(STORE_META).delete(META_KEY_ACCOUNT));
  }

  async listPasskeyUnlocks(): Promise<PasskeyUnlockRecord[]> {
    const db = await this.#db();
    const tx = db.transaction(STORE_META, 'readonly');
    const value = await requestToPromise(tx.objectStore(STORE_META).get(META_KEY_PASSKEY_UNLOCKS));
    // Un magasin ecrit par une version plus recente ne doit pas casser l'ecran
    // de deverrouillage : ce qui n'est pas un tableau est traite comme vide.
    return Array.isArray(value) ? (value as PasskeyUnlockRecord[]) : [];
  }

  async savePasskeyUnlocks(records: readonly PasskeyUnlockRecord[]): Promise<void> {
    await writeTx(await this.#db(), STORE_META, (tx) =>
      tx.objectStore(STORE_META).put([...records], META_KEY_PASSKEY_UNLOCKS),
    );
  }

  async loadDeviceId(): Promise<string | null> {
    const db = await this.#db();
    const tx = db.transaction(STORE_META, 'readonly');
    const value = await requestToPromise(tx.objectStore(STORE_META).get(META_KEY_DEVICE_ID));
    return typeof value === 'string' ? value : null;
  }

  async saveDeviceId(deviceId: string): Promise<void> {
    await writeTx(await this.#db(), STORE_META, (tx) => tx.objectStore(STORE_META).put(deviceId, META_KEY_DEVICE_ID));
  }

  async listItems(): Promise<EncryptedVaultItem[]> {
    const db = await this.#db();
    const tx = db.transaction(STORE_ITEMS, 'readonly');
    return (await requestToPromise(tx.objectStore(STORE_ITEMS).getAll())) as EncryptedVaultItem[];
  }

  async putItem(item: EncryptedVaultItem): Promise<void> {
    await writeTx(await this.#db(), STORE_ITEMS, (tx) => tx.objectStore(STORE_ITEMS).put(item));
  }

  async putItems(items: readonly EncryptedVaultItem[]): Promise<void> {
    await writeTx(await this.#db(), STORE_ITEMS, (tx) => {
      const store = tx.objectStore(STORE_ITEMS);
      for (const item of items) store.put(item);
    });
  }

  async listFolders(): Promise<EncryptedVaultFolder[]> {
    const db = await this.#db();
    const tx = db.transaction(STORE_FOLDERS, 'readonly');
    return (await requestToPromise(tx.objectStore(STORE_FOLDERS).getAll())) as EncryptedVaultFolder[];
  }

  async putFolder(folder: EncryptedVaultFolder): Promise<void> {
    await writeTx(await this.#db(), STORE_FOLDERS, (tx) => tx.objectStore(STORE_FOLDERS).put(folder));
  }

  async putFolders(folders: readonly EncryptedVaultFolder[]): Promise<void> {
    await writeTx(await this.#db(), STORE_FOLDERS, (tx) => {
      const store = tx.objectStore(STORE_FOLDERS);
      for (const folder of folders) store.put(folder);
    });
  }

  async listAttachments(itemId?: string): Promise<EncryptedVaultAttachment[]> {
    const db = await this.#db();
    const store = db.transaction(STORE_ATTACHMENTS, 'readonly').objectStore(STORE_ATTACHMENTS);
    const request = itemId === undefined ? store.getAll() : store.index(INDEX_ATTACHMENT_ITEM).getAll(itemId);
    return (await requestToPromise(request)) as EncryptedVaultAttachment[];
  }

  async putAttachments(attachments: readonly EncryptedVaultAttachment[]): Promise<void> {
    await writeTx(await this.#db(), STORE_ATTACHMENTS, (tx) => {
      const store = tx.objectStore(STORE_ATTACHMENTS);
      for (const attachment of attachments) store.put(attachment);
    });
  }

  async deleteRecords(ids: {
    readonly items?: readonly string[];
    readonly folders?: readonly string[];
    readonly attachments?: readonly string[];
  }): Promise<void> {
    await writeTx(await this.#db(), [STORE_ITEMS, STORE_FOLDERS, STORE_ATTACHMENTS], (tx) => {
      for (const id of ids.items ?? []) tx.objectStore(STORE_ITEMS).delete(id);
      for (const id of ids.folders ?? []) tx.objectStore(STORE_FOLDERS).delete(id);
      for (const id of ids.attachments ?? []) tx.objectStore(STORE_ATTACHMENTS).delete(id);
    });
  }

  async clearAll(): Promise<void> {
    await writeTx(await this.#db(), [STORE_ITEMS, STORE_FOLDERS, STORE_ATTACHMENTS, STORE_META], (tx) => {
      tx.objectStore(STORE_ITEMS).clear();
      tx.objectStore(STORE_FOLDERS).clear();
      tx.objectStore(STORE_ATTACHMENTS).clear();
      tx.objectStore(STORE_META).clear();
    });
  }
}

export function isIndexedDbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}
